/**
 * The guided listing setup: the seven steps in BR-107 order, the contract every step component
 * implements, and the "where to resume" rule. The order never varies with what is already filled in.
 */

import type { Ref } from 'react';
import type { StepRailStatus } from '../../../components/layout';
import type { Listing } from '../../../lib/leasing.api';

export const GUIDE_STEPS = [
  { key: 'marketing', label: 'Marketing' },
  { key: 'details', label: 'Listing details' },
  { key: 'showings', label: 'Showings' },
  { key: 'contact', label: 'Contact and screening' },
  { key: 'agent', label: 'AI leasing agent' },
  { key: 'preview', label: 'Preview' },
  { key: 'review', label: 'Review and publish' },
] as const;

export type GuideStepKey = (typeof GUIDE_STEPS)[number]['key'];

/** Steps that can be opened before the draft Listing exists (the Marketing step needs only the Unit). */
export const STEPS_BEFORE_LISTING: readonly GuideStepKey[] = ['marketing', 'details'];

export function isGuideStepKey(value: string | undefined): value is GuideStepKey {
  return GUIDE_STEPS.some((step) => step.key === value);
}

export function guideStepLabel(key: GuideStepKey): string {
  return GUIDE_STEPS.find((step) => step.key === key)!.label;
}

/** A step's imperative handle: "Save and continue" calls save(); the leave prompt reads isDirty(). */
export interface GuideStepHandle {
  /** Persist whatever is edited. Resolves true when saved (or nothing to save), false when it failed
   *  or is invalid; the step shows its own inline error in that case. */
  save(): Promise<boolean>;
  isDirty(): boolean;
}

/** Props every step component receives from ListingGuidePage. */
export interface GuideStepProps {
  unitId: string;
  /** null until Listing details is first saved. */
  listing: Listing | null;
  handleRef: Ref<GuideStepHandle>;
  /** Fires when the step's unsaved-edits flag flips (drives the "Unsaved changes" badge). */
  onDirtyChange: (dirty: boolean) => void;
  /** Fires while the step is saving (drives "Saving…" on the action bar). */
  onSavingChange: (saving: boolean) => void;
  /** Listing details only: called once with the draft Listing the first save created. */
  onListingCreated?: (listing: Listing) => void;
  /** Showings only: the primary action reads "Continue" while nothing on screen can be saved. */
  onPrimaryLabelChange?: (label: string | null) => void;
}

export type GuideStepStatus = StepRailStatus;

export interface GuideStepState {
  status: GuideStepStatus;
  /** One line read from saved data only. */
  summary: string;
  /** The data this step's state depends on is still loading (render a skeleton, not a guess). */
  loading: boolean;
}

/**
 * Where the guide opens: the first step, in BR-107 order, whose completion predicate is false,
 * skipping Preview; Review and publish when every other predicate is true. `done` is the predicate.
 */
export function firstIncompleteStep(done: Readonly<Record<GuideStepKey, boolean>>): GuideStepKey {
  for (const step of GUIDE_STEPS) {
    if (step.key === 'preview' || step.key === 'review') continue;
    if (!done[step.key]) return step.key;
  }
  return 'review';
}
